import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import MuiDrawer from '@mui/material/Drawer';
import MuiBox from '@mui/material/Box';
import { styled, alpha } from '@mui/material/styles';
import { noop } from 'lodash';
import 'react';
import { ReactComponent as ForwardRef } from '../../assets/icons/CloseSmall.svg.js';
import useScrollCapture from '../../hooks/useScrollCapture/useScrollCapture.js';
import 'react-highlight-words';
import useKey from '../../hooks/useKey.js';
import getScrollFadeStyles from '../../utils/getScrollFadeStyles.js';
import 'date-fns';
import ConditionalPanelResizable from './ConditionalPanelResizable.js';

const _Drawer = styled(MuiDrawer, {
    shouldForwardProp: prop => !['rtl', 'placementGap', 'resizableWidth', 'resizer', 'nonStaticPosition'].includes(prop),
}) `
  height: 100%;
  .MuiDrawer-paper {
    overflow-x: hidden;
    min-width: 289px;
    border: none;
    ${({ theme, rtl, placementGap, open, resizableWidth }) => `
      ${!resizableWidth ? `width: 289px;` : ''}
      display: ${open ? 'flex' : 'none'};
      flex-direction: column;
      top: ${placementGap.top};
      bottom: ${placementGap.bottom};
      height: calc(100% - ${placementGap.top});
      direction: ${rtl ? 'rtl' : 'ltr'};
      background-color: ${theme.palette.N80.main};
      color: ${theme.palette.N10.main};
      box-shadow: -10px 10px 20px ${alpha(theme.palette.N90.main, 0.6)};
    `}

    > :last-of-type {
      margin-top: auto;
    }
  }
`;
const Panel = styled(MuiBox, { shouldForwardProp: prop => prop !== 'rtl' }) `
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  height: 34px;
  width: 100%;
  ${({ theme, rtl }) => `
    direction: ${rtl === 'true' ? 'rtl' : 'ltr'};
    background-color: ${theme.palette.N70.main};
    border-bottom: 1px solid ${theme.palette.N50.main};
    svg {
      &:hover {
        > path {
          fill: ${theme.palette.primary.main};
        }
      }
    }
  `}
`;
const ScrollableContent = styled(MuiBox, {
    shouldForwardProp: prop => !['rtl', 'withTopShadow', 'withBottomShadow', 'scrollPosition'].includes(prop),
}) `
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: overlay;
  flex-grow: 1;
  height: calc(100% - 34px);
  @-moz-document url-prefix() {
    overflow: auto;
  }

  ${({ rtl }) => `
    direction: ${rtl ? 'rtl' : 'ltr'};
  `}

  ${({ theme, scrollPosition, withTopShadow, withBottomShadow }) => `
    transition: box-shadow linear ${theme.transitions.duration.fast};
    padding: ${theme.spacing(0, 0, 5, 0)};
    ${scrollPosition && `box-shadow: ${getScrollFadeStyles(theme, withTopShadow, withBottomShadow)[scrollPosition]}`};
  `}
`;
const _CloseSvg = styled(ForwardRef) `
  align-self: center;
  ${({ theme }) => `
    path {
      fill: ${theme.palette.N30.main};
    }
  `}
`;
const CloseWrapper = styled(MuiBox) `
  display: flex;
  cursor: pointer;
  ${({ theme }) => `
    padding: ${theme.spacing(2)};
  `}
`;
const TopPanel = ({ onCloseClick, rtl }) => (jsx(Panel, Object.assign({ rtl: rtl === null || rtl === void 0 ? void 0 : rtl.toString() }, { children: jsx(CloseWrapper, Object.assign({ "data-testid": "close-button", onClick: onCloseClick }, { children: jsx(_CloseSvg, {}) })) })));
const isNonStaticPositionPanel = (props) => {
    return 'nonStaticPosition' in props && !!props['nonStaticPosition'];
};
const isResizablePanel = (props, isNonStatic) => {
    if ('resizer' in props && !isNonStatic) {
        // eslint-disable-next-line no-console
        console.debug('InspectorPanel must have nonStaticPosition set to true in order to be resizable.');
    }
    return isNonStatic && 'resizer' in props;
};
const InspectorPanel = (props) => {
    var _a, _b;
    const { header, footer, children, onCloseClick, rtl, 'data-testid': testId = 'inspector-panel', placementGap = { top: '0', bottom: '0' } } = props, rest = __rest(props, ["header", "footer", "children", "onCloseClick", "rtl", 'data-testid', "placementGap"]);
    const isNonStatic = isNonStaticPositionPanel(props);
    const isResizable = isResizablePanel(props, isNonStatic);
    const { resizableWidth, setResizableWidth, minResizeWidth, maxResizeWidth } = isResizable
        ? props.resizer
        : {
            resizableWidth: undefined,
            setResizableWidth: undefined,
            minResizeWidth: undefined,
            maxResizeWidth: undefined,
        };
    const PaperProps = isNonStatic
        ? Object.assign(Object.assign({}, props.PaperProps), { sx: Object.assign(Object.assign({}, (_a = props.PaperProps) === null || _a === void 0 ? void 0 : _a.sx), { position: 'relative', width: resizableWidth }) }) : props.PaperProps;
    const { scrollRef, scrollPosition } = useScrollCapture();
    useKey('Escape', onCloseClick);
    const panelSide = rtl ? 'left' : 'right';
    return (jsx(ConditionalPanelResizable, Object.assign({ condition: isResizable, maxWidth: maxResizeWidth ? maxResizeWidth : 800, minWidth: minResizeWidth ? minResizeWidth : 300, panelSide: panelSide, setPanelWidth: setResizableWidth ? setResizableWidth : noop, width: resizableWidth ? resizableWidth : 0 }, { children: jsxs(_Drawer, Object.assign({ variant: "persistent", anchor: rtl ? 'left' : 'right', "data-testid": testId, className: `styled-scrollbar ${rest.className}`, rtl: rtl, placementGap: placementGap, 
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            PaperProps: Object.assign({ 'data-testid': `${testId}-panel` }, PaperProps), resizableWidth: (_b = rest.resizer) === null || _b === void 0 ? void 0 : _b.resizableWidth }, rest, { children: [jsx(TopPanel, { onCloseClick: onCloseClick, rtl: rtl === null || rtl === void 0 ? void 0 : rtl.toString() }), header, jsx(ScrollableContent, Object.assign({ className: "styled-scrollbar", ref: scrollRef, withBottomShadow: true, withTopShadow: true, scrollPosition: scrollPosition, rtl: rtl }, { children: children })), footer] })) })));
};

export { InspectorPanel as default };
//# sourceMappingURL=InspectorPanel.js.map
